import { useState, useRef } from 'react';
import {
  Button,
  ButtonGroup,
  Tooltip,
  IconButton,
  Snackbar,
  Snack,
  TableHeading,
  Table,
} from '@nike/eds';
import classnames from 'classnames';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLoaderData, useRevalidator } from 'react-router-dom';

import Loader from 'components/Loader';
import Input from 'components/Input';
import HotsearchRankingDetail from 'components/Hotsearch/HotsearchRankingDetail';
import RankingListMetaDataAddAndEdit from 'components/Hotsearch/RankingListMetaDataAddAndEdit';
import { atom } from 'jotai';

import { QUERY_KEYS } from '../../constants';
import HotsearchClient from '../../clientAPI/hotsearch';
import propagate403Error from '../../utils/propagate403Error';
import { isExistScope } from '../../utils/authHelpers';

const previewAtom = atom<RankingList | null>(null);

const editAtom = atom<RankingList | null>(null);

export default function HotSearchRankingLists() {
  const queryClient = useQueryClient();
  const pageSize = 10;
  const loaderData = useLoaderData() as { access_token: string };
  const jwt = loaderData.access_token;
  const canCreateRank = isExistScope('search360:hotsearch:create', jwt);
  const revalidator = useRevalidator();

  const titleInputRef = useRef<HTMLInputElement>(null);
  const [searchTitle, setSearchTitle] = useState('');
  const [page, setPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [showSnack, setShowSnack] = useState(false);

  const {
    status,
    data: responseRank,
    error,
  } = useQuery({
    queryKey: [QUERY_KEYS.HOTSEARCH_ALL_RANKING_LISTS, page, searchTitle],
    queryFn: () => {
      return HotsearchClient.findRankingLists({
        currentPage: page,
        pageSize: pageSize,
        title: searchTitle,
      });
    },
    keepPreviousData: true,
    staleTime: Infinity,
    onError: (err: { status: number }) =>
      err.status === 401 && revalidator.revalidate(),
  });

  const onSearch = () => {
    setSearchTitle(titleInputRef?.current?.value.trim() || '');
    setPage(1);
  };

  const updateItem = (msg: string) => {
    setShowSnack(true);
    setErrorMsg(msg);
    refresh();
  };
  const refresh = async () => {
    queryClient.invalidateQueries();
  };
  const onDismiss = () => {
    setShowModal(false);
  };

  const hasMoreData =
    Math.ceil((responseRank ? responseRank.total : 0) / pageSize) > page;
  const noPagination = page === 1 && !hasMoreData;

  const deleteRankById = async (item: RankingList): Promise<void> => {
    console.log('start delete');
    try {
      await HotsearchClient.deleteRank(item._id).then(() => {
        refresh().then(() => {
          setShowSnack(true);
          setErrorMsg('Delete Success');
        });
      });
    } catch (errors) {
      setShowSnack(true);
    }
  };

  const afterPublished = () => {
    console.log('end publish and refresh current page');
    refresh().then(() => {
      setShowSnack(true);
      setErrorMsg('Publish Success');
    });
  };

  propagate403Error(error as keyable);

  const headers = [
    {
      Header: 'Order',
      className: classnames('tw-w-[5%]'),
    },
    {
      Header: 'Title/Name',
      className: classnames('tw-w-[13%]'),
    },
    {
      Header: 'Type',
      className: classnames('tw-w-[13%]'),
    },
    {
      Header: 'Status',
      className: classnames('tw-w-[13%]'),
    },
    {
      Header: 'Effect Date',
      className: classnames('tw-w-[13%]'),
    },
    {
      Header: 'Concepts',
      className: classnames('tw-w-[13%]'),
    },
    {
      Header: 'Channel',
      className: classnames('tw-w-[13%]'),
    },
    {
      Header: 'Operation',
      className: classnames('tw-w-[17%]'),
    },
  ];

  return (
    <div className='tw-w-full tw-mx-auto tw-px-4'>
      {/* <p className='eds-type--display-5 tw-mb-4'>Ranking Lists</p> */}
      <div className='tw-flex  tw-px-1 tw-mt-4'>
        <Input
          placeholder='Title'
          className='tw-border-b tw-w-44 tw-mr-4'
          ref={titleInputRef}
          onSave={onSearch}
        />
        <Tooltip bodySlot='Search' enableFocus>
          <IconButton
            icon='Search'
            size='small'
            variant='primary'
            disableRipple={true}
            onClick={onSearch}
            label='Search'
          />
        </Tooltip>
      </div>
      {status === 'loading' ? (
        <Loader />
      ) : (
        <div>
          <div style={{ overflow: 'scroll' }}>
            <Table className='tw-w-full tw-mt-5'>
              <thead>
                <tr>
                  {/* <TableHeading
                    className='tw-w-[5%]'
                    // style={{ minWidth: 100 }}
                  >
                    Order
                  </TableHeading> */}
                  {headers.map((column) => (
                    <TableHeading
                      key={crypto.randomUUID()}
                      className={column.className}
                    >
                      {column.Header}
                    </TableHeading>
                  ))}
                  {/* <TableHeading className='tw-w-[10%]'>Operation</TableHeading> */}
                </tr>
              </thead>
              <tbody>
                {/* {rows.map(row => (
                  <tr>
                    {row.map(cell => {
                      return cell;
                    })}
                  </tr>
                ))} */}

                {responseRank?.rankings?.map((item) => (
                  <HotsearchRankingDetail
                    key={item._id}
                    item={item}
                    previewAtom={previewAtom}
                    editAtom={editAtom}
                    deleteRankById={deleteRankById}
                    updateItem={updateItem}
                    afterPublished={afterPublished}
                  />
                ))}
              </tbody>
            </Table>
            {/* {responseRank?.rankings?.map((item) => (
              <HotsearchRankingDetail
                key={item._id}
                item={item}
                previewAtom={previewAtom}
                editAtom={editAtom}
                deleteRankById={deleteRankById}
                updateItem={updateItem}
                afterPublished={afterPublished}
              />
            ))} */}
            {responseRank?.rankings?.length === 0 && page === 1 && (
              <h2 className='eds-type--display-5 tw-py-32 tw-text-center'>
                No Data available
              </h2>
            )}
          </div>

          <div className='tw-flex tw-items-center tw-justify-between tw-pb-40'>
            {!noPagination && (
              <p className='tw-text-sm tw-font-bold'>Current Page: {page}</p>
            )}
            <ButtonGroup>
              {page !== 1 && (
                <Button size='small' onClick={() => setPage(page - 1)}>
                  Previous Page
                </Button>
              )}
              {hasMoreData && (
                <Button size='small' onClick={() => setPage(page + 1)}>
                  Next Page
                </Button>
              )}
            </ButtonGroup>
          </div>
        </div>
      )}

      <Snackbar style={{ left: '90%', top: '90%' }}>
        {showSnack && (
          <Snack
            autoDismissDuration={5000}
            id='pw-error-notification'
            onDismiss={(_id) => {
              setShowSnack(false);
            }}
            status='success'
          >
            {errorMsg ? errorMsg : 'success'}
          </Snack>
        )}
      </Snackbar>

      {showModal && (
        <RankingListMetaDataAddAndEdit
          itemId=''
          showModal={showModal}
          onDismiss={onDismiss}
          updateItem={updateItem}
          mode={'ADD'}
          canWrite={canCreateRank}
        />
      )}

      {canCreateRank && (
        <div className='tw-fixed tw-bottom-10 tw-right-20'>
          <IconButton
            icon='Plus'
            label='New'
            onClick={() => {
              setShowModal(true);
            }}
          />
        </div>
      )}
    </div>
  );
}
